"use client";

export const dynamic = 'force-dynamic';

import { useEffect, useState } from "react";
import { useMediaQuery } from '@mantine/hooks';

interface ChatMonitorScope {
  scopeType: 'source' | 'merge_group';
  scopeKey: string;
  displayName: string;
  lastSummaryAt?: string | null;
  lastSummaryPreview?: string | null;
}

interface ChatMonitorSummary {
  id: string;
  scopeType: string;
  scopeKey: string;
  periodType: 'hourly' | 'daily' | 'weekly';
  periodStart: string;
  periodEnd: string;
  summaryText: string;
  createdAt: string;
}

const PERIODS: { value: 'hourly' | 'daily' | 'weekly'; label: string }[] = [
  { value: 'hourly', label: 'Theo giờ (hôm nay)' },
  { value: 'daily', label: 'Theo ngày (tuần này)' },
  { value: 'weekly', label: 'Theo tuần' },
];

export default function ChatMonitorPage() {
  const isMobile = useMediaQuery('(max-width: 768px)');

  const [scopes, setScopes] = useState<ChatMonitorScope[]>([]);
  const [loadingScopes, setLoadingScopes] = useState(true);
  const [selected, setSelected] = useState<ChatMonitorScope | null>(null);
  const [period, setPeriod] = useState<'hourly' | 'daily' | 'weekly'>('hourly');
  const [summaries, setSummaries] = useState<ChatMonitorSummary[]>([]);
  const [loadingSummaries, setLoadingSummaries] = useState(false);

  const [showAdd, setShowAdd] = useState(false);
  const [displayName, setDisplayName] = useState('');
  const [scraperLookupKey, setScraperLookupKey] = useState('');
  const [mergeGroupKey, setMergeGroupKey] = useState('');
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);

  async function loadScopes() {
    setLoadingScopes(true);
    try {
      const res = await fetch('/api/chat-monitor/scopes');
      const json = await res.json();
      setScopes(json.success && Array.isArray(json.data) ? json.data : []);
    } finally {
      setLoadingScopes(false);
    }
  }

  useEffect(() => {
    loadScopes();
  }, []);

  useEffect(() => {
    if (!selected) return;
    setLoadingSummaries(true);
    const params = new URLSearchParams({
      scopeType: selected.scopeType,
      scopeKey: selected.scopeKey,
      periodType: period,
    });
    fetch(`/api/chat-monitor/summaries?${params.toString()}`)
      .then((res) => res.json())
      .then((json) => setSummaries(json.success && Array.isArray(json.data) ? json.data : []))
      .finally(() => setLoadingSummaries(false));
  }, [selected, period]);

  async function handleAdd() {
    setAddError(null);
    if (!displayName.trim()) {
      setAddError('Nhập tên hiển thị');
      return;
    }
    setAdding(true);
    try {
      const res = await fetch('/api/chat-monitor/sources', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          displayName: displayName.trim(),
          scraperLookupKey: scraperLookupKey.trim() || displayName.trim(),
          mergeGroupKey: mergeGroupKey.trim() || undefined,
        }),
      });
      const json = await res.json();
      if (!json.success) {
        setAddError(json.message || 'Thêm thất bại');
        return;
      }
      setShowAdd(false);
      setDisplayName('');
      setScraperLookupKey('');
      setMergeGroupKey('');
      await loadScopes();
    } finally {
      setAdding(false);
    }
  }

  const scopeList = (
    <div style={{ width: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, gap: 8 }}>
        <strong>Nguồn đang theo dõi</strong>
        <button style={{ padding: '8px 12px', minHeight: 36 }} onClick={() => setShowAdd((v) => !v)}>
          {showAdd ? 'Đóng' : '+ Thêm'}
        </button>
      </div>

      {showAdd && (
        <div style={{ border: '1px solid #333', borderRadius: 6, padding: 10, marginBottom: 12 }}>
          <div style={{ marginBottom: 6 }}>
            <input
              placeholder="Tên hiển thị (vd: Alita - Abc (FAD))"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              style={{ width: '100%', padding: 8, minHeight: 40, boxSizing: 'border-box' }}
            />
          </div>
          <div style={{ marginBottom: 6 }}>
            <input
              placeholder="Tên chính xác trên Zalo (để trống nếu giống tên hiển thị)"
              value={scraperLookupKey}
              onChange={(e) => setScraperLookupKey(e.target.value)}
              style={{ width: '100%', padding: 8, minHeight: 40, boxSizing: 'border-box' }}
            />
          </div>
          <div style={{ marginBottom: 6 }}>
            <input
              placeholder="Gộp chung với nhóm khác (tuỳ chọn)"
              value={mergeGroupKey}
              onChange={(e) => setMergeGroupKey(e.target.value)}
              style={{ width: '100%', padding: 8, minHeight: 40, boxSizing: 'border-box' }}
            />
          </div>
          {addError && <div style={{ color: '#e33', marginBottom: 6 }}>{addError}</div>}
          <button style={{ padding: '8px 14px', minHeight: 40, width: isMobile ? '100%' : 'auto' }} onClick={handleAdd} disabled={adding}>
            {adding ? 'Đang thêm...' : 'Thêm'}
          </button>
        </div>
      )}

      {loadingScopes ? (
        <div>Đang tải...</div>
      ) : scopes.length === 0 ? (
        <div style={{ color: '#888' }}>Chưa theo dõi chat nào</div>
      ) : (
        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {scopes.map((s) => (
            <li
              key={`${s.scopeType}:${s.scopeKey}`}
              onClick={() => setSelected(s)}
              style={{
                padding: 12,
                borderRadius: 6,
                cursor: 'pointer',
                marginBottom: 8,
                background:
                  selected && selected.scopeKey === s.scopeKey && selected.scopeType === s.scopeType
                    ? 'rgba(255,255,255,0.08)'
                    : 'transparent',
                border: '1px solid #333',
              }}
            >
              <div style={{ fontWeight: 600, wordBreak: 'break-word' }}>
                {s.displayName}
                {s.scopeType === 'merge_group' && (
                  <span style={{ fontSize: 11, marginLeft: 6, color: '#6cf' }}>Gộp nhiều nguồn</span>
                )}
              </div>
              <div style={{ fontSize: 12, color: '#888' }}>
                {s.lastSummaryAt ? new Date(s.lastSummaryAt).toLocaleString() : 'Chưa có dữ liệu'}
              </div>
              {s.lastSummaryPreview && (
                <div style={{ fontSize: 12, color: '#aaa', marginTop: 4, wordBreak: 'break-word' }}>
                  {s.lastSummaryPreview}
                  {s.lastSummaryPreview.length >= 200 ? '…' : ''}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );

  const detailView = !selected ? (
    <div style={{ color: '#888' }}>{isMobile ? '' : 'Chọn 1 nguồn bên trái để xem tóm tắt'}</div>
  ) : (
    <>
      {isMobile && (
        <button style={{ padding: '8px 12px', minHeight: 36, marginBottom: 10 }} onClick={() => setSelected(null)}>
          ← Danh sách
        </button>
      )}
      <h3 style={{ marginTop: 0, wordBreak: 'break-word' }}>{selected.displayName}</h3>
      <div style={{ marginBottom: 12, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
        {PERIODS.map((p) => (
          <button
            key={p.value}
            onClick={() => setPeriod(p.value)}
            style={{
              padding: '8px 10px',
              minHeight: 36,
              fontWeight: period === p.value ? 700 : 400,
              textDecoration: period === p.value ? 'underline' : 'none',
            }}
          >
            {p.label}
          </button>
        ))}
      </div>

      {loadingSummaries ? (
        <div>Đang tải...</div>
      ) : summaries.length === 0 ? (
        <div style={{ color: '#888' }}>Chưa có tóm tắt nào cho khoảng thời gian này</div>
      ) : (
        summaries.map((item) => (
          <div key={item.id} style={{ border: '1px solid #333', borderRadius: 6, padding: 12, marginBottom: 10 }}>
            <div style={{ fontWeight: 600, marginBottom: 4, fontSize: isMobile ? 13 : 14 }}>
              {new Date(item.periodStart).toLocaleString()} — {new Date(item.periodEnd).toLocaleString()}
            </div>
            <div style={{ whiteSpace: 'pre-wrap' }}>{item.summaryText}</div>
          </div>
        ))
      )}
    </>
  );

  return (
    <div style={{ padding: isMobile ? 12 : 20 }} className="dashboard-container">
      <h1 style={{ fontSize: isMobile ? 20 : undefined }}>Chat Monitor Legacy</h1>
      <p style={{ color: '#888', marginTop: -8 }}>
        Màn hình legacy cho dữ liệu chat-monitor cũ. Pipeline Zalo chủ động đã bị tắt; trang này chỉ còn để xem dữ liệu lịch sử.
      </p>

      {isMobile ? (
        <div style={{ marginTop: 16 }}>{selected ? detailView : scopeList}</div>
      ) : (
        <div style={{ display: 'flex', gap: 20, marginTop: 16, alignItems: 'flex-start' }}>
          <div style={{ flex: '0 0 340px', minWidth: 0 }}>{scopeList}</div>
          <div style={{ flex: 1, minWidth: 0 }}>{detailView}</div>
        </div>
      )}
    </div>
  );
}
